import {
  DataDrivenPropertyValueSpecification,
  ExpressionSpecification,
  FilterSpecification,
  LayerProps,
} from 'shared/map-exports';
import { NorthEastIcon, SouthEastIcon } from 'features/mapping/icons';
import {
  DEFAULT_VIEW_STATE,
  SELECTED_FEATURE_HIGHLIGHT_COLOR,
} from 'features/mapping/mapping.constants';
import { TFunction } from 'i18next';
import { TOOLTIP_LABEL_LAYOUT_STYLES } from 'features/mapping/mapping.styles';
import { FLOOD_CATEGORIES } from '../../waterLevel.types';
import {
  MAX_ICON_ZOOM_LEVEL,
  MIN_NONE_OFFLINE_ICON_ZOOM_LEVEL,
  MIN_LABEL_ZOOM_LEVEL,
  SOURCE_LAYER,
  BASE_PULSE_CIRCLE_RADIUS_LARGE,
  BASE_PULSE_CIRCLE_RADIUS_SMALL,
} from './WaterLevelLayer.constants';

const iconOffsetSpecification: DataDrivenPropertyValueSpecification<
  [number, number]
> = [0, -9];

const iconSizeSpecification: DataDrivenPropertyValueSpecification<number> = [
  'interpolate',
  ['linear'],
  ['zoom'],
  DEFAULT_VIEW_STATE.minZoom,
  0.75,
  MAX_ICON_ZOOM_LEVEL,
  1,
  13,
  1.25,
];

/**
 * Gauges are "truly none" when they have no flood category
 * definitions and no current flood category.
 */
const isNoneFloodCategoryFilter: FilterSpecification = [
  'all',
  ['==', ['get', 'flood_category'], FLOOD_CATEGORIES.none],
  ['==', ['get', 'has_flood_categories'], false],
];

/**
 * Gauges are categorized as "normal" either because they are
 * explicitly marked as such, or because they have flood category
 * definitions but are currently not in any flood stage.
 */
const isNormalFloodCategoryFilter: FilterSpecification = [
  'any',
  ['==', ['get', 'flood_category'], FLOOD_CATEGORIES.normal],
  [
    'all',
    ['==', ['get', 'flood_category'], FLOOD_CATEGORIES.none],
    ['==', ['get', 'has_flood_categories'], true],
  ],
];

const hasForecastCategoryFilter: FilterSpecification = [
  'all',
  ['has', 'peak_forecast_flood_category'],
  // Handle the case where the property exists but is set to 'none'
  ['!=', ['get', 'peak_forecast_flood_category'], FLOOD_CATEGORIES.none],
  // Per AC/design: do not display forecast icons for water level gauges
  // whose current flood category is 'none' or missing (defaults to 'none' in other expressions).
  ['!', isNoneFloodCategoryFilter],
];

const isOfflineFilter: FilterSpecification = [
  '==',
  ['get', 'is_offline'],
  true,
];

const isOnlineFilter: FilterSpecification = ['!', isOfflineFilter];

/**
 * Builds the icon name expression for water level map features based on
 * current and forecasted flood levels.
 *
 * This logic is directly tied to the icon asset naming scheme defined in
 * `features/mapping/icons/iconData/waterLevelIcons.ts`. Update this implementation if
 * those naming conventions change.
 */
const getIconExpression = (): DataDrivenPropertyValueSpecification<string> => {
  const formatProp = (
    prop: string,
  ): DataDrivenPropertyValueSpecification<string> => {
    // Default to 'none' if property is missing, then capitalize the first letter
    const value = ['coalesce', ['get', prop], 'none'];
    return ['concat', ['upcase', ['slice', value, 0, 1]], ['slice', value, 1]];
  };

  // Determine base prefix: 'Offline' or capitalized 'flood_category'
  const category = [
    'case',
    isOfflineFilter,
    'Offline',
    isNormalFloodCategoryFilter,
    'Normal',
    formatProp('flood_category'),
  ];

  return [
    'case',
    hasForecastCategoryFilter,
    // Format: CategoryForecastPeakCategoryIcon (e.g., MinorForecastMajorIcon)
    [
      'concat',
      category,
      'Forecast',
      formatProp('peak_forecast_flood_category'),
      'Icon',
    ],
    // Format: CategoryWaterLevelIcon (e.g., MinorWaterLevelIcon)
    ['concat', category, 'WaterLevelIcon'],
  ];
};

const getIconSortKey = (): DataDrivenPropertyValueSpecification<number> => {
  /**
   * Defines rendering order for gauge icons. Higher values are drawn on top because we are allowing icon overlap.
   * Priority: Major(6) > Moderate(5) > Minor(4) > Action(3) > Normal(2) > Offline w/ Forecast(1.5) > None(1) > Offline(0)
   */
  const getRank = (
    prop: string,
  ): DataDrivenPropertyValueSpecification<number> => [
    'match',
    ['coalesce', ['get', prop], 'none'],
    'major',
    6,
    'moderate',
    5,
    'minor',
    4,
    'action',
    3,
    'normal',
    2,
    'none',
    1,
    0,
  ];

  return [
    'case',
    isOfflineFilter,
    // If offline but has forecast, place above 'none' (1), otherwise at the very bottom
    ['case', hasForecastCategoryFilter, 1.5, 0],
    [
      'case',
      hasForecastCategoryFilter,
      getRank('peak_forecast_flood_category'),
      isNormalFloodCategoryFilter,
      2,
      getRank('flood_category'),
    ],
  ];
};

const commonIconStyle: LayerProps = {
  type: 'symbol',
  'source-layer': SOURCE_LAYER,
  layout: {
    'icon-image': getIconExpression(),
    'icon-offset': iconOffsetSpecification,
    'icon-allow-overlap': true,
    'icon-size': iconSizeSpecification,
    'symbol-sort-key': getIconSortKey(),
  },
};

const withinCoverageAreaFilter: FilterSpecification = [
  '==',
  ['get', 'is_within_coverage_area'],
  true,
];

const lowZoomIconAllowedCategories = [
  FLOOD_CATEGORIES.major,
  FLOOD_CATEGORIES.moderate,
  FLOOD_CATEGORIES.minor,
  FLOOD_CATEGORIES.action,
];

const lowZoomForecastIconFilter: FilterSpecification = [
  'all',
  // Per AC/design: do not display forecast icons for water level gauges
  // whose current flood category is 'none'.
  ['!', isNoneFloodCategoryFilter],
  [
    'in',
    ['get', 'peak_forecast_flood_category'],
    ['literal', lowZoomIconAllowedCategories],
  ],
];

const lowZoomIconFilter: FilterSpecification = [
  'any',
  ['in', ['get', 'flood_category'], ['literal', lowZoomIconAllowedCategories]],
  lowZoomForecastIconFilter,
];

export const getLowZoomIconStyle = (isInternalUser: boolean): LayerProps => {
  const filter: FilterSpecification = [
    'all',
    lowZoomIconFilter,
    // Needed because any flood category / peak category returned for offline station would be outdated
    isOnlineFilter,
  ];
  if (!isInternalUser) {
    filter.push(withinCoverageAreaFilter);
  }
  const style: LayerProps = {
    ...commonIconStyle,
    maxzoom: MAX_ICON_ZOOM_LEVEL,
    filter,
  };
  return style;
};

export const getHighZoomIconStyle = (isInternalUser: boolean): LayerProps => {
  // Filter out none and offline icons - they have their own layer starting at zoom 10
  const filter: FilterSpecification = [
    'all',
    ['!', isNoneFloodCategoryFilter],
    ['!', isOfflineFilter],
  ];
  if (!isInternalUser) {
    filter.push(withinCoverageAreaFilter);
  }

  const style: LayerProps = {
    ...commonIconStyle,
    minzoom: MAX_ICON_ZOOM_LEVEL,
    filter,
  };
  return style;
};

export const getNoneOfflineIconStyle = (
  isInternalUser: boolean,
): LayerProps => {
  // Only show none and offline icons, starting at zoom 10
  const filter: FilterSpecification = [
    'any',
    isNoneFloodCategoryFilter,
    isOfflineFilter,
  ];
  if (!isInternalUser) {
    filter.push(withinCoverageAreaFilter);
  }

  const style: LayerProps = {
    ...commonIconStyle,
    minzoom: MIN_NONE_OFFLINE_ICON_ZOOM_LEVEL,
    filter,
  };
  return style;
};

const getNumberExpression = (propKey: string): ExpressionSpecification => [
  'number-format',
  ['get', propKey],
  { 'min-fraction-digits': 0, 'max-fraction-digits': 1 },
];

// Same precision as `getNumberExpression` (one decimal place) but stays numeric for `>` / `!=`.
const getLevelRoundedForComparison = (
  propKey: string,
): ExpressionSpecification => [
  '/',
  ['round', ['*', ['to-number', ['get', propKey]], 10]],
  10,
];

const displayForecastFilter: FilterSpecification = [
  'all',
  hasForecastCategoryFilter,
  ['has', 'peak_forecast_level'],
  // Exclude features where forecast level equals current level, if any
  [
    '!=',
    getLevelRoundedForComparison('peak_forecast_level'),
    getLevelRoundedForComparison('level'),
  ],
];

const iconImageExpression: ExpressionSpecification = [
  'case',
  [
    '>',
    getLevelRoundedForComparison('peak_forecast_level'),
    getLevelRoundedForComparison('level'),
  ],
  NorthEastIcon.name, // Rising: Peak forecast exceeds current level
  SouthEastIcon.name, // Falling/Steady: Forecast is <= current (Equal case filtered by displayForecastFilter)
];

const displayLabelFilter: FilterSpecification = [
  'any',
  isOfflineFilter,
  ['all', ['has', 'level'], ['==', ['get', 'is_observation_recent'], true]],
];

export const getLabelStyle = (
  isInternalUser: boolean,
  t: TFunction,
): LayerProps => {
  const filter: FilterSpecification = isInternalUser
    ? displayLabelFilter
    : ['all', displayLabelFilter, withinCoverageAreaFilter];
  return {
    type: 'symbol',
    minzoom: MIN_LABEL_ZOOM_LEVEL,
    'source-layer': 'weatherstations_waterlevel',
    layout: {
      ...TOOLTIP_LABEL_LAYOUT_STYLES,
      'text-field': [
        'case',
        isOfflineFilter,
        ['format', ['literal', t('common.offline')]],
        displayForecastFilter,
        [
          'format',
          getNumberExpression('level'),
          ['literal', ' ft'],
          { 'font-scale': 0.8 },
          ['literal', ' '],
          ['image', iconImageExpression],
          ['literal', ' '],
          getNumberExpression('peak_forecast_level'),
          ['literal', ' ft'],
          { 'font-scale': 0.8 },
        ],
        // Default: Current Level only
        [
          'format',
          getNumberExpression('level'),
          ['literal', ' ft'],
          { 'font-scale': 0.8 },
        ],
      ],
      'text-offset': ['case', hasForecastCategoryFilter, [0, 2.25], [0, 1.75]],
      'icon-allow-overlap': true,
      'symbol-sort-key': getIconSortKey(),
    },
    paint: {
      'icon-color': '#FFFFFF',
    },
    filter,
  };
};

export const getSelectedPointIconStyle = (
  activeGaugeId?: number,
): LayerProps => {
  const filter: FilterSpecification = ['==', ['id'], activeGaugeId ?? null];

  return {
    type: 'circle',
    'source-layer': SOURCE_LAYER,
    paint: {
      'circle-radius': 0,
      'circle-color': SELECTED_FEATURE_HIGHLIGHT_COLOR,
      'circle-blur': 1,
      // Match icon offset so pulse is centered on the icon
      'circle-translate': [0, -9],
    },
    filter,
  };
};

export const pulseBaseRadiusExpression: ExpressionSpecification = [
  'case',
  // Has forecast (and not "truly none")
  [
    'all',
    ['has', 'peak_forecast_flood_category'],
    ['!=', ['get', 'peak_forecast_flood_category'], FLOOD_CATEGORIES.none],
    [
      'any',
      ['!=', ['get', 'flood_category'], FLOOD_CATEGORIES.none],
      ['==', ['get', 'has_flood_categories'], true],
    ],
  ],
  BASE_PULSE_CIRCLE_RADIUS_LARGE,
  // Flood stage categories (action, minor, moderate, major)
  [
    'in',
    ['get', 'flood_category'],
    ['literal', ['action', 'minor', 'moderate', 'major']],
  ],
  BASE_PULSE_CIRCLE_RADIUS_LARGE,
  // Default: small icons (none, normal, offline)
  BASE_PULSE_CIRCLE_RADIUS_SMALL,
];
